/**
 * Jurídico: licenciamento e royalties contratuais (ADR 0065, E2, D-24). Os termos vêm do contrato
 * e são só informados: nada de percentual, valor ou critério presumido. Valores contratual,
 * declarado e efetivamente recebido ou pago ficam separados; não há contabilização.
 */
import { z } from 'zod';
import { toScaled } from './money.ts';

export const IP_LICENSE_DIRECTIONS = ['outbound', 'inbound'] as const;
export const IP_LICENSE_MODALITIES = ['license', 'assignment'] as const;
export const IP_LICENSE_EXCLUSIVITY = ['exclusive', 'non_exclusive'] as const;
export const IP_LICENSE_RENEWALS = ['automatic', 'by_agreement', 'none'] as const;
export const IP_REMUNERATION_KINDS = [
  'royalty_percentage',
  'fixed_amount',
  'minimum_guarantee',
  'percentage_with_minimum',
  'no_remuneration',
  'other',
] as const;
export const IP_LICENSE_PERIODICITIES = [
  'monthly',
  'quarterly',
  'semiannual',
  'annual',
  'one_time',
  'other',
] as const;
export const IP_LICENSE_STATUSES = ['negotiation', 'active', 'expired', 'terminated'] as const;
export const ROYALTY_REPORT_STATUSES = ['expected', 'declared', 'settled', 'disputed'] as const;

/** Tipos de remuneração que exigem o percentual ou o valor do contrato. */
export const REMUNERATION_NEEDS: Record<
  (typeof IP_REMUNERATION_KINDS)[number],
  { rate: boolean; amount: boolean }
> = {
  royalty_percentage: { rate: true, amount: false },
  fixed_amount: { rate: false, amount: true },
  minimum_guarantee: { rate: false, amount: true },
  percentage_with_minimum: { rate: true, amount: true },
  no_remuneration: { rate: false, amount: false },
  other: { rate: false, amount: false },
};

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const decimal = (msg = 'Use números com ponto (ex.: 1500.00)') =>
  z
    .string()
    .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, msg)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const currency = z
  .string()
  .regex(/^([A-Z]{3})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const legalIpLicenseInput = z
  .object({
    legalEntityId: z.uuid(),
    contractId: z.uuid('Escolha o contrato'),
    direction: z.enum(IP_LICENSE_DIRECTIONS),
    modality: z.enum(IP_LICENSE_MODALITIES),
    licensorName: z.string().trim().min(1, 'Informe quem licencia ou cede').max(300),
    licensorPartnerId: id,
    licenseeName: z.string().trim().min(1, 'Informe quem recebe').max(300),
    licenseePartnerId: id,
    exclusivity: z.enum(IP_LICENSE_EXCLUSIVITY).nullable().optional(),
    territory: text(500),
    purpose: text(4000),
    usageLimits: text(4000),
    startDate: day,
    endDate: day,
    renewal: z.enum(IP_LICENSE_RENEWALS).nullable().optional(),
    renewalNote: text(1000),
    remunerationKind: z.enum(IP_REMUNERATION_KINDS).nullable().optional(),
    royaltyRate: z
      .string()
      .regex(/^(\d{1,3}(\.\d{1,4})?)?$/, 'Use o percentual com ponto (ex.: 7.5)')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    fixedAmount: decimal(),
    currency,
    calculationBasis: text(2000),
    periodicity: z.enum(IP_LICENSE_PERIODICITIES).nullable().optional(),
    reportingTerms: text(2000),
    paymentTerms: text(2000),
    /** Adiantamento contra royalties (A5), na moeda da licença. */
    advanceAmount: decimal(),
    advancePaidOn: day,
    ownerId: id,
    status: z.enum(IP_LICENSE_STATUSES).default('active'),
    confidentiality: z.enum(['restricted', 'confidential']).default('restricted'),
    notes: text(10000),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.startDate && v.endDate && v.endDate < v.startDate) {
      issue('endDate', 'O fim não pode ser antes do início');
    }
    const needs = v.remunerationKind ? REMUNERATION_NEEDS[v.remunerationKind] : null;
    if (needs?.rate && !v.royaltyRate) issue('royaltyRate', 'Informe o percentual do contrato');
    if (v.royaltyRate && (Number(v.royaltyRate) <= 0 || Number(v.royaltyRate) > 100)) {
      issue('royaltyRate', 'O percentual fica entre 0 e 100');
    }
    if (needs?.amount && !v.fixedAmount) issue('fixedAmount', 'Informe o valor do contrato');
    if (v.fixedAmount && !v.currency) issue('currency', 'Informe a moeda');
    if (v.advanceAmount && !v.currency) issue('currency', 'Informe a moeda');
    if (v.advanceAmount && toScaled(v.advanceAmount) <= 0n) {
      issue('advanceAmount', 'O adiantamento precisa ser maior que zero');
    }
    if (v.advancePaidOn && !v.advanceAmount) {
      issue('advanceAmount', 'Informe o valor do adiantamento');
    }
  });
export type LegalIpLicenseInput = z.infer<typeof legalIpLicenseInput>;

export const legalIpLicenseWorkInput = z.object({ workId: z.uuid('Escolha a obra') });

/** Prestação de contas de um período: os três valores são informados, nunca calculados. */
export const legalRoyaltyReportInput = z
  .object({
    periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe o início do período'),
    periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe o fim do período'),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Informe a moeda'),
    contractualAmount: decimal(),
    declaredBase: decimal(),
    declaredAmount: decimal(),
    declaredOn: day,
    paymentDueDate: day,
    settledAmount: decimal(),
    settledOn: day,
    /** Parte do royalty declarado retida para recuperar o adiantamento (A5). */
    recoupedAmount: decimal(),
    status: z.enum(ROYALTY_REPORT_STATUSES).default('expected'),
    note: text(4000),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.periodEnd < v.periodStart) issue('periodEnd', 'O fim não pode ser antes do início');
    if (!!v.declaredAmount !== !!v.declaredOn) {
      issue(v.declaredAmount ? 'declaredOn' : 'declaredAmount', 'Informe o valor e a data juntos');
    }
    if (!!v.settledAmount !== !!v.settledOn) {
      issue(v.settledAmount ? 'settledOn' : 'settledAmount', 'Informe o valor e a data juntos');
    }
    if (v.status === 'declared' && !v.declaredAmount) {
      issue('declaredAmount', 'Informe o valor declarado');
    }
    if (v.recoupedAmount && !v.declaredAmount) {
      issue('recoupedAmount', 'Informe antes o valor declarado');
    }
    if (
      v.recoupedAmount &&
      v.declaredAmount &&
      toScaled(v.recoupedAmount) > toScaled(v.declaredAmount)
    ) {
      issue('recoupedAmount', 'O retido não passa do valor declarado');
    }
    if (v.status === 'settled' && !v.settledAmount) {
      issue('settledAmount', 'Informe o valor recebido ou pago');
    }
  });
export type LegalRoyaltyReportInput = z.infer<typeof legalRoyaltyReportInput>;
